import { type ReactNode, useCallback, useMemo } from "react";
import {
  type PanelLayoutChoice,
  useGetPanelLayoutPreferencesQuery,
  useSavePanelLayoutPreferenceMutation,
} from "_shared/_views/panels/_api/panel-layout-preferences-api";
import {
  type PanelLayoutPreferences,
  PanelLayoutPreferencesContext,
} from "_shared/_views/panels/panel-layout-preferences-context";
import { useSession } from "users/__session/session-context";

/**
 * Hands every panel below it the reader's saved width and collapsed state, and saves new ones (#6621).
 * One request answers for every panel, as `TableColumnPreferencesProvider` does for tables.
 */
export function PanelLayoutPreferencesProvider({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const isLoggedIn = Boolean(session?.isLoggedIn);
  const query = useGetPanelLayoutPreferencesQuery({ session }, { skip: !isLoggedIn });
  const [save] = useSavePanelLayoutPreferenceMutation();

  const savedPanels = useMemo(() => {
    // A failed answer is no answer: the panel opens as it does for a reader who never chose.
    if (!isLoggedIn || !query.data || query.isError) return undefined;
    return new Map<string, PanelLayoutChoice>(
      query.data.panels.map((panel) => [panel.panelKey, { width: panel.width ?? null, isCollapsed: panel.isCollapsed }]),
    );
  }, [isLoggedIn, query.data, query.isError]);

  const savePanel = useCallback(
    (panelKey: string, choice: PanelLayoutChoice) => {
      if (!session?.isLoggedIn) return;
      save({ session, panelKey, ...choice })
        .unwrap()
        .catch((error: unknown) => {
          // The panel already shows the choice; it simply does not follow the reader elsewhere.
          console.warn(`Could not save how the ${panelKey} panel is kept.`, error);
        });
    },
    [save, session],
  );

  const value = useMemo<PanelLayoutPreferences>(() => ({ savedPanels, savePanel }), [savedPanels, savePanel]);

  return <PanelLayoutPreferencesContext.Provider value={value}>{children}</PanelLayoutPreferencesContext.Provider>;
}
